
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { TrendChart } from "@/components/keyword-research/TrendChart";
import { KeywordSuggestion } from "@/hooks/useKeywordAssistant";
import { Plus, TrendingUp } from "lucide-react";

interface KeywordAssistantResultsProps {
  suggestions: KeywordSuggestion[];
  onAddSelected: (selectedKeywords: string[]) => void;
  isLoading: boolean;
}

export const KeywordAssistantResults = ({ 
  suggestions, 
  onAddSelected, 
  isLoading 
}: KeywordAssistantResultsProps) => {
  const [selectedKeywords, setSelectedKeywords] = useState<string[]>([]);

  const handleKeywordToggle = (keyword: string) => {
    setSelectedKeywords(prev => 
      prev.includes(keyword) 
        ? prev.filter(k => k !== keyword)
        : [...prev, keyword]
    );
  };

  const handleAddSelected = () => {
    onAddSelected(selectedKeywords);
    setSelectedKeywords([]);
  };

  const handleSelectAll = () => {
    const allKeywords = suggestions.map(s => s.keyword);
    setSelectedKeywords(allKeywords);
  };

  const getDifficultyColor = (difficulty: number) => {
    if (difficulty <= 30) return "bg-green-100 text-green-800";
    if (difficulty <= 60) return "bg-yellow-100 text-yellow-800";
    return "bg-red-100 text-red-800";
  };

  const getCompetitionLevel = (competition: number) => {
    if (competition <= 30) return { level: "Low", color: "bg-green-100 text-green-800" };
    if (competition <= 70) return { level: "Medium", color: "bg-yellow-100 text-yellow-800" };
    return { level: "High", color: "bg-red-100 text-red-800" };
  };

  console.log("KeywordAssistantResults received suggestions:", suggestions);

  return (
    <Card className="shadow-lg border-0 bg-white/80 backdrop-blur-sm">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Keyword Research Results ({suggestions.length})
          </CardTitle>
          {selectedKeywords.length > 0 && (
            <Button 
              onClick={handleAddSelected}
              disabled={isLoading}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add {selectedKeywords.length} Selected
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {suggestions.length === 0 ? (
          <p className="text-gray-500 text-center py-8">
            No keyword suggestions found. Try different keywords.
          </p>
        ) : (
          <div className="space-y-4">
            <div className="flex justify-between items-center mb-4">
              <Button 
                onClick={handleSelectAll}
                variant="outline"
                disabled={isLoading || selectedKeywords.length === suggestions.length}
              >
                Select All
              </Button>
              {selectedKeywords.length > 0 && (
                <span className="text-sm text-gray-600">
                  {selectedKeywords.length} of {suggestions.length} selected
                </span>
              )}
            </div>
            {suggestions.map((suggestion, index) => {
              const competitionInfo = getCompetitionLevel(suggestion.competition);
              console.log(`Keyword: ${suggestion.keyword}, Trend data:`, suggestion.trend);
              
              return (
                <div key={index} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      checked={selectedKeywords.includes(suggestion.keyword)}
                      onCheckedChange={() => handleKeywordToggle(suggestion.keyword)}
                      className="mt-1"
                    />
                    
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-medium text-gray-900">{suggestion.keyword}</h3>
                          {suggestion.country && (
                            <Badge variant="outline" className="text-xs">
                              {suggestion.country}
                            </Badge>
                          )}
                        </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                        <div className="text-center">
                          <div className="text-lg font-bold text-blue-600">
                            {suggestion.searchVolume?.toLocaleString() || 'N/A'}
                          </div>
                          <div className="text-xs text-gray-500">Search Volume</div>
                        </div>
                        
                        <div className="text-center">
                          <Badge className={getDifficultyColor(suggestion.difficulty)}>
                            {suggestion.difficulty || 0}
                          </Badge>
                          <div className="text-xs text-gray-500 mt-1">Difficulty</div>
                        </div>
                        
                        <div className="text-center">
                          <div className="text-lg font-bold text-green-600">
                            ${suggestion.cpc?.toFixed(2) || '0.00'}
                          </div>
                          <div className="text-xs text-gray-500">CPC</div>
                        </div>
                        
                        <div className="text-center">
                          <Badge className={competitionInfo.color}>
                            {competitionInfo.level}
                          </Badge>
                          <div className="text-xs text-gray-500 mt-1">Competition</div>
                        </div>
                      </div>

                      {/* Show trend chart if data is available */}
                      {suggestion.trend && suggestion.trend.length > 0 && (
                        <div className="mt-4">
                          <TrendChart 
                            data={suggestion.trend} 
                            keyword={suggestion.keyword} 
                          />
                        </div>
                      )}

                      {/* Show related keywords if available */}
                      {suggestion.relatedKeywords && suggestion.relatedKeywords.length > 0 && (
                        <div className="mt-3">
                          <h4 className="text-sm font-medium text-gray-700 mb-2">Related Keywords:</h4>
                          <div className="flex flex-wrap gap-1">
                            {suggestion.relatedKeywords.slice(0, 4).map((relatedKeyword, idx) => (
                              <Badge key={idx} variant="secondary" className="text-xs">
                                {relatedKeyword}
                              </Badge>
                            ))}
                            {suggestion.relatedKeywords.length > 4 && (
                              <Badge variant="outline" className="text-xs">
                                +{suggestion.relatedKeywords.length - 4} more
                              </Badge>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
